import { useEffect, useState } from "react";
import { Circle, Line, Rect, Layer } from "react-konva";
import { computeVisibilityPolygon, isPointInPolygon } from "../lib/visibility";

// Fog of war / dynamic lighting / darkness / vision-radius logic for MapCanvas, extracted
// verbatim (Phase 42 refactor) — no behavior changes. Owns the light-flicker animation tick
// and every "who can see what" computation, plus the two render functions for the fog and
// darkness-tint layers.
export function useMapVision({ game, scene, tokens, walls, myMemberId, isGm, gridSize, width, height, dragPreview }) {
  const [flickerTick, setFlickerTick] = useState(0);
  const anyLights = tokens.some((t) => (t.light_radius_ft || 0) > 0);
  const anyPivotingCones = tokens.some((t) => t.sight_cone_enabled && t.sight_pivot_enabled);

  useEffect(() => {
    if (!anyLights && !anyPivotingCones) return;
    const id = setInterval(() => setFlickerTick((t) => t + 1), 180);
    return () => clearInterval(id);
  }, [anyLights, anyPivotingCones]);

  const myTokens = tokens.filter((t) => t.created_by === myMemberId && t.is_approved);
  const darknessMode = scene?.darkness_mode || "none";
  // GM-only "Full Sight" toggle: reveals the whole map to players exactly as the GM sees it,
  // bypassing fog-of-war/vision/darkness entirely for this specific scene.
  const gmView = isGm || !!scene?.full_sight;
  // Wall shadow-casting applies in every vision mode whenever the scene actually has walls
  // drawn — it's simply the more correct way to compute what a token can see, not something
  // exclusive to the "Dynamic Lighting" toggle. Falls back to a plain radius circle otherwise.
  const useWallLighting = (walls || []).length > 0;
  const lightSources = tokens.filter((t) => (t.light_radius_ft || 0) > 0);

  // Live drag position takes priority over the possibly-stale store value.
  const posFor = (t) => (dragPreview && dragPreview.tokenId === t.id ? { x: dragPreview.x, y: dragPreview.y } : { x: t.x, y: t.y });

  // Vision priority, per the GM's own three modes (only one applies at a time):
  //  1. Darkness (scene.darkness_mode != none) — tightest: everyone but darkvision/see-in-darkness
  //     is limited to their own ~5ft personal light radius (or a carried light source's radius).
  //  2. Fog of War / Dynamic Lighting toggles — existing GM-controlled radius rules, unchanged.
  //  3. Normal viewing (neither above is on) — the map reads as fully lit, but a character still
  //     can't see indefinitely far across a big battle map; a generous default vision distance applies.
  const getVisionRadius = (t) => {
    const visionType = t.vision_type || "normal";
    const hasDark = visionType.includes("darkvision");
    const hasLowLight = visionType.includes("low-light");
    const seesInDarkness = visionType.includes("see-in-darkness");
    const baseRadius = hasDark ? gridSize * 12 : game?.dynamic_lighting_enabled ? gridSize * 6 : gridSize * 8;
    if (darknessMode === "none") return baseRadius;
    if (darknessMode === "magical") return seesInDarkness ? baseRadius : gridSize; // magical darkness blinds even darkvision except "see in darkness"
    // non-magical darkness: darkvision (or see-in-darkness) sees fully, low-light gets half, everyone else is limited to their own ~5ft personal light
    if (hasDark || seesInDarkness) return baseRadius;
    if (hasLowLight) return baseRadius * 0.5;
    return gridSize; // ~5ft, personal light radius only
  };

  // A lit room reveals its layout/walls to everyone in it (see renderLightHoles), but a token
  // (other than your own) being merely inside a lit area is no longer enough to see IT — light
  // reveals terrain, not creatures. Ambient light-source props stay visible (they ARE the light),
  // and anything explicitly "revealed" (e.g. a spotted gang-mate) bypasses this like before.
  const canSeeToken = (t) => {
    if (gmView) return true;
    if (t.created_by === myMemberId) return true;
    if (t.token_type === "light") return true;
    if ((t.revealed_to_member_ids || []).includes(myMemberId)) return true;
    const tp = posFor(t);
    return myTokens.some((mt) => {
      const mp = posFor(mt);
      const radius = getVisionRadius(mt);
      if (Math.hypot(tp.x - mp.x, tp.y - mp.y) > radius) return false;
      if (!useWallLighting) return true;
      const poly = computeVisibilityPolygon(mp, walls, radius, { x: 0, y: 0, width, height });
      return isPointInPolygon(poly, tp.x, tp.y);
    });
  };

  // Torches/candles (or any token with a light radius) punch a hole through darkness/FOW for
  // EVERYONE nearby, regardless of who owns the token — unlike vision, light is environmental.
  // A subtle deterministic flicker (seeded per-token so multiple torches don't pulse in sync)
  // keeps the light feeling alive rather than a static hole.
  const flickerFor = (tokenId) => {
    let seed = 0;
    for (let i = 0; i < tokenId.length; i++) seed = (seed * 31 + tokenId.charCodeAt(i)) % 1000;
    return Math.sin(flickerTick * 0.7 + seed) * 0.08;
  };

  const renderLightHoles = () =>
    lightSources.map((t) => {
      const p = posFor(t);
      const radius = ((t.light_radius_ft || 0) / 5) * gridSize * (1 + flickerFor(t.id));
      if (useWallLighting) {
        const poly = computeVisibilityPolygon(p, walls, radius, { x: 0, y: 0, width, height });
        return <Line key={`light-${t.id}`} points={poly} closed fill="#000000" globalCompositeOperation="destination-out" />;
      }
      return <Circle key={`light-${t.id}`} x={p.x} y={p.y} radius={radius} fill="#000000" globalCompositeOperation="destination-out" />;
    });

  // NPCs that have "given away" their position (a gang-mate got attacked/spotted) punch a small
  // hole right on themselves for that specific viewer, regardless of light/LoS — deliberately
  // ignores walls (this is "you now know roughly where they are", not real sight).
  const gangRevealedTokens = tokens.filter((t) => (t.revealed_to_member_ids || []).includes(myMemberId));
  const renderGangRevealHoles = () =>
    gangRevealedTokens.map((t) => (
      <Circle key={`gang-${t.id}`} x={t.x} y={t.y} radius={(t.size || 50) * 0.9} fill="#000000" globalCompositeOperation="destination-out" />
    ));

  // Shared by both the always-on vision-cutoff layer and the atmospheric darkness tint layer,
  // so a viewer's own tokens always punch an identically-shaped hole (radius from getVisionRadius,
  // real wall shadow-casting when the scene has walls) no matter which of the two layers it's in.
  const renderMyVisionHoles = () =>
    myTokens.map((t) => {
      const p = posFor(t);
      const radius = getVisionRadius(t);
      if (useWallLighting) {
        const poly = computeVisibilityPolygon(p, walls, radius, { x: 0, y: 0, width, height });
        return <Line key={`vis-${t.id}`} points={poly} closed fill="#000000" globalCompositeOperation="destination-out" />;
      }
      return <Circle key={`vis-${t.id}`} x={p.x} y={p.y} radius={radius} fill="#000000" globalCompositeOperation="destination-out" />;
    });

  // Vision is now ALWAYS restricted for non-GM viewers (previously this whole layer only
  // rendered when the GM had explicitly flipped the Fog of War/Dynamic Lighting switches on,
  // meaning a totally untouched scene had zero visibility limit at all). getVisionRadius already
  // resolves the correct radius for whichever of the three modes is currently active.
  const renderFogLayer = () => {
    if (gmView) return null;
    return (
      <Layer listening={false}>
        <Rect x={0} y={0} width={width} height={height} fill="#000000" opacity={0.92} globalCompositeOperation="source-over" />
        {renderMyVisionHoles()}
        {renderLightHoles()}
        {renderGangRevealHoles()}
      </Layer>
    );
  };

  const renderDarknessTint = () => {
    if (darknessMode === "none") return null;
    const color = darknessMode === "magical" ? "#2b0a3d" : "#0a1420";
    return (
      <Layer listening={false}>
        <Rect x={0} y={0} width={width} height={height} fill={color} opacity={darknessMode === "magical" ? 0.35 : 0.25} globalCompositeOperation="multiply" />
        {!gmView && renderMyVisionHoles()}
        {renderLightHoles()}
        {renderGangRevealHoles()}
      </Layer>
    );
  };

  return { gmView, darknessMode, useWallLighting, myTokens, posFor, getVisionRadius, canSeeToken, renderFogLayer, renderDarknessTint };
}
